/** Pure injury classification shared by the browser and Node tests. */

import type { LeagueConfig, Player, PlayerAvailabilityDetail, SeasonOutOverride } from './types.ts';

export function isExcludedFromCurrentSeason(
  availability: PlayerAvailabilityDetail | undefined,
  leagueType: LeagueConfig['league_type'],
): boolean {
  return leagueType !== 'dynasty' && availability?.status === 'season_out';
}

export function classifyPlayerAvailability(
  player: Player,
  override?: SeasonOutOverride,
): PlayerAvailabilityDetail {
  if (override?.designation === 'season_out') {
    return {
      status: 'season_out',
      reason: override.reason,
      source: 'override',
      source_name: override.source_name,
      source_url: override.source_url,
      reported_at: override.reported_at,
    };
  }

  const hasInjury = Boolean(
    player.injury_status ||
    player.injury_body_part ||
    player.injury_notes ||
    player.practice_participation ||
    player.practice_description,
  );

  return {
    status: hasInjury ? 'injured' : 'healthy',
    reason: override?.reason ?? (hasInjury ? injuryReason(player) : null),
    source: override ? 'override' : 'sleeper',
    source_name: override?.source_name ?? (hasInjury ? 'Sleeper player status' : null),
    source_url: override?.source_url ?? (hasInjury ? 'https://docs.sleeper.com/' : null),
    reported_at: override?.reported_at ?? (hasInjury && player.news_updated ? new Date(player.news_updated).toISOString() : null),
  };
}

export function injuryReason(player: Player): string | null {
  const details = [
    player.injury_status,
    player.injury_body_part,
    player.injury_notes,
    player.practice_participation,
  ].filter((value): value is string => Boolean(value?.trim()));
  return details.length ? details.join(' · ') : null;
}
